<script setup lang="ts">
// PROPS
interface Props {
    offerAmount: number;
    buyNowPrice: number;
}

const { offerAmount, buyNowPrice } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();

// DATA
const successProbability = computed(() => getSuccessProbability(offerAmount, buyNowPrice));

type ProbabilityLevel = 'guaranteed' | 'veryHigh' | 'high' | 'mediumHigh' | 'medium' | 'mediumLow' | 'low';

const probabilityLevel = computed<ProbabilityLevel>(() => {
    const p = successProbability.value;
    if (p >= 100) return 'guaranteed';
    if (p >= 85) return 'veryHigh';
    if (p >= 75) return 'high';
    if (p >= 62) return 'mediumHigh';
    if (p >= 57) return 'medium';
    if (p >= 50) return 'mediumLow';

    return 'low';
});

const title = computed(() =>
    tt(`directSaleNegotiation.acceptanceProbability.${probabilityLevel.value}` as const, {
        probability: Math.round(successProbability.value),
    })
);

const barColor = computed<string>(() => {
    const map: Record<ProbabilityLevel, string> = {
        guaranteed: 'bg-green-700',
        veryHigh: 'bg-green-600',
        high: 'bg-green-400',
        mediumHigh: 'bg-lime-400',
        medium: 'bg-yellow-400',
        mediumLow: 'bg-amber-500',
        low: 'bg-red-500',
    };

    return map[probabilityLevel.value];
});

// METHODS
/**
 * Piecewise linear approximation of the offer acceptance probability curve.
 * Breakpoints are chosen to align with the color-threshold transitions.
 */
function getSuccessProbability(offerAmount: number, buyNowPrice: number): number {
    if (buyNowPrice <= 0) {
        return 0;
    }

    if (offerAmount >= buyNowPrice) {
        return 100;
    }

    const ratio = (offerAmount / buyNowPrice) * 100;

    let probability: number;

    if (ratio >= 91) {
        probability = 1.6433 * ratio - 64.33;
    } else if (ratio >= 76) {
        probability = 1.284 * ratio - 31.634;
    } else if (ratio >= 62) {
        probability = 1.0893 * ratio - 16.837;
    } else {
        probability = 1.85 * ratio - 64;
    }

    return Math.min(100, Math.max(1, probability));
}
</script>

<template>
    <div class="pb-4">
        <p class="text-highlighted text-sm font-medium">{{ title }}</p>
        <UProgress class="mt-2" :model-value="successProbability" :ui="{ indicator: barColor }" />
    </div>
</template>
